// src/services/ideaService.js
import { collection, onSnapshot, addDoc, updateDoc, deleteDoc, doc, query, where, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { encryptIdea, decryptIdea, decryptSubtask, decryptIfNeeded } from '../utils/encryption';
import { normalizeUrl } from '../utils/utils';

const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY;

/**
 * Subscribe to real-time updates for a user's ideas
 * @param {string} userId - The user ID
 * @param {Function} onNext - Callback function called with decrypted ideas array on each update
 * @param {Function} onError - Callback function called on errors
 * @returns {Function} Unsubscribe function to stop listening
 */
export const subscribeToIdeas = (userId, onNext, onError) => {
  if (!userId) {
    onError(new Error('User ID is required'));
    return () => {};
  }

  const ideasRef = collection(db, 'users', userId, 'ideas');
  
  // Use onSnapshot for real-time updates
  const unsubscribe = onSnapshot(
    ideasRef,
    (snapshot) => {
      try {
        const ideasData = snapshot.docs.map(docSnapshot => {
          const data = docSnapshot.data();
          const decryptedIdea = decryptIdea(data, ENCRYPTION_KEY);
          
          // Normalize rating to ensure it's a number (some may be stored as strings)
          const rating = decryptedIdea.rating;
          const normalizedRating = typeof rating === 'string' ? parseInt(rating, 10) || 0 : (rating || 0);
          
          // Normalize URLs in resources (for backward compatibility with old data)
          const normalizedResources = (decryptedIdea.resources || []).map(resource => ({
            ...resource,
            url: resource.url ? normalizeUrl(resource.url) : resource.url
          }));
          
          // Normalize URLs in subtask links (for backward compatibility with old data)
          const normalizedSubtasks = data.subtasks ? data.subtasks.map(subtask => {
            const decrypted = decryptSubtask(subtask, ENCRYPTION_KEY);
            return {
              ...decrypted,
              link: decrypted.link ? normalizeUrl(decrypted.link) : decrypted.link
            };
          }) : [];
          
          return {
            id: docSnapshot.id,
            ...decryptedIdea,
            rating: normalizedRating,
            resources: normalizedResources,
            subtasks: normalizedSubtasks,
            // Development tracking fields (default to "raw" for existing ideas without these fields)
            developmentStatus: data.developmentStatus || 'raw',
            developmentPath: data.developmentPath || null,
            lastDevelopedAt: data.lastDevelopedAt || null,
            // Timestamp fields for "NEW" badge and sorting
            createdAt: data.createdAt || null,
            updatedAt: data.updatedAt || null
          };
        });
        
        onNext(ideasData);
      } catch (error) {
        console.error('Error processing ideas snapshot:', error);
        onError(error);
      }
    },
    (error) => {
      console.error('Error in ideas subscription:', error);
      onError(error);
    }
  );

  return unsubscribe;
};

/**
 * Subscribe to real-time updates for a user's tags
 * @param {string} userId - The user ID
 * @param {Function} onNext - Callback function called with tags object on each update
 * @param {Function} onError - Callback function called on errors
 * @returns {Function} Unsubscribe function to stop listening
 */
export const subscribeToTags = (userId, onNext, onError) => {
  if (!userId) {
    onError(new Error('User ID is required'));
    return () => {};
  }

  const tagsRef = collection(db, 'tags');
  const tagsQuery = query(tagsRef, where('userId', '==', userId));
  
  // Use onSnapshot for real-time updates
  const unsubscribe = onSnapshot(
    tagsQuery,
    (snapshot) => {
      try {
        const tagsData = snapshot.docs.reduce((acc, docSnapshot) => {
          const data = docSnapshot.data();
          acc[docSnapshot.id] = decryptIfNeeded(data.name, ENCRYPTION_KEY);
          return acc;
        }, {});
        
        onNext(tagsData);
      } catch (error) {
        console.error('Error processing tags snapshot:', error);
        onError(error);
      }
    },
    (error) => {
      console.error('Error in tags subscription:', error);
      onError(error);
    }
  );

  return unsubscribe;
};

/**
 * Create a new idea
 * @param {string} userId - The user ID
 * @param {Object} ideaData - The idea data to create
 * @returns {Promise<string>} The document ID of the created idea
 */
export const createIdea = async (userId, ideaData) => {
  if (!userId) {
    throw new Error('User ID is required');
  }

  // Encrypt the idea data before saving
  const encryptedData = encryptIdea({
    idea: ideaData.idea.trim(),
    description: ideaData.description.trim(),
    tags: ideaData.tags || [],
    status: ideaData.status || 'Drafted',
    rating: ideaData.rating || '0',
    subtasks: ideaData.subtasks || [],
    lessonsLearned: ideaData.lessonsLearned || '',
    resources: ideaData.resources || [],
    color: ideaData.color || null
  }, ENCRYPTION_KEY);

  // Add the idea to Firestore with development tracking fields
  const ideasRef = collection(db, 'users', userId, 'ideas');
  const docRef = await addDoc(ideasRef, {
    ...encryptedData,
    // Development tracking fields (not encrypted - used for queries/display)
    developmentStatus: ideaData.developmentStatus || 'raw',
    developmentPath: ideaData.developmentPath || null,
    lastDevelopedAt: ideaData.lastDevelopedAt || null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });

  return docRef.id;
};

/**
 * Update an existing idea
 * @param {string} userId - The user ID
 * @param {string} ideaId - The idea document ID
 * @param {Object} ideaData - The updated idea data
 * @returns {Promise<void>}
 */
export const updateIdea = async (userId, ideaId, ideaData) => {
  if (!userId || !ideaId) {
    throw new Error('User ID and Idea ID are required');
  }

  const ideaRef = doc(db, 'users', userId, 'ideas', ideaId);

  // Encrypt the idea data before saving
  const encryptedData = encryptIdea({
    idea: ideaData.idea.trim(),
    description: ideaData.description.trim(),
    tags: ideaData.tags || [],
    status: ideaData.status || 'Drafted',
    rating: ideaData.rating || 0,
    subtasks: ideaData.subtasks || [],
    lessonsLearned: ideaData.lessonsLearned || '',
    resources: ideaData.resources || [],
    color: ideaData.color || null
  }, ENCRYPTION_KEY);

  // Build update object with development tracking fields
  const updateData = {
    ...encryptedData,
    updatedAt: serverTimestamp()
  };

  // Only include development fields if provided (preserve existing values otherwise)
  if (ideaData.developmentStatus !== undefined) {
    updateData.developmentStatus = ideaData.developmentStatus;
  }
  if (ideaData.developmentPath !== undefined) {
    updateData.developmentPath = ideaData.developmentPath;
  }
  if (ideaData.lastDevelopedAt !== undefined) {
    updateData.lastDevelopedAt = ideaData.lastDevelopedAt;
  }

  // Update Firestore with retry logic
  const maxRetries = 3;
  let retryCount = 0;
  
  while (retryCount < maxRetries) {
    try {
      await updateDoc(ideaRef, updateData);
      return; // Success - exit retry loop
    } catch (error) {
      retryCount++;
      console.error(`Attempt ${retryCount} failed:`, error);
      if (retryCount === maxRetries) throw error;
      await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
    }
  }
};

/**
 * Delete an idea
 * @param {string} userId - The user ID
 * @param {string} ideaId - The idea document ID
 * @returns {Promise<void>}
 */
export const deleteIdea = async (userId, ideaId) => {
  if (!userId || !ideaId) {
    throw new Error('User ID and Idea ID are required');
  }

  const ideaRef = doc(db, 'users', userId, 'ideas', ideaId);
  await deleteDoc(ideaRef);
};
